import { faBath } from '@fortawesome/pro-light-svg-icons/faBath';
import { faBedAlt } from '@fortawesome/pro-light-svg-icons/faBedAlt';
import { faCouch } from '@fortawesome/pro-light-svg-icons/faCouch';
import { faEnvelope } from '@fortawesome/pro-light-svg-icons/faEnvelope';
import { faPhone } from '@fortawesome/pro-light-svg-icons/faPhone';
import { faEyeSlash } from '@fortawesome/pro-regular-svg-icons/faEyeSlash';
import { PRIME_BG_COLOR_MAP, PRIME_COLOR_MAP } from '@propertypal/shared/src/constants/prime';
import noPhoto from '@propertypal/shared/src/resources/fallbacks/no-photo-icon.png';
import { GOLD } from '@propertypal/shared/src/styles/theme';
import { Property } from '@propertypal/shared/src/types/property';
import { PropertySearchResult } from '@propertypal/shared/src/types/propertySearch';
import { formatNumber } from '@propertypal/shared/src/utils/floatToCurrency';
import platform from '@propertypal/shared/src/utils/platform';
import { getAgentLogos, getMainImage, getThumbImages } from '@propertypal/shared/src/utils/property';
import getSellingAgent from '@propertypal/shared/src/utils/property/getSellingAgent';
import isHighlightedDevelopment from '@propertypal/shared/src/utils/property/highlightedDevelopment';
import { SubHeading, Text } from '@propertypal/web-ui/src/typography';
import React, { FunctionComponent, MouseEventHandler } from 'react';
import { useTheme } from 'styled-components';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import LazyImage from '../image/LazyImage';
import PropertyPrice from '../property/PropertyPrice';
import StartEndTime from '../time/StartEndTime';
import AttributePills from './AttributePills';
import EnergyRatingIcon from './EnergyRatingIcon';
import OpenViewingOverlay from './OpenViewingOverlay';
import PinnedDevelopmentPropertyCarousel from './PinnedDevelopmentPropertyCarousel';
import PropertyBadge from './PropertyBadge';
import PropertyBanner from './PropertyBanner';
import {
  AccountLogo,
  AccountLogos,
  AgentHeader,
  BottomRow,
  ContactText,
  Container,
  Content,
  DeveloperLogos,
  DevelopmentBoxFiller,
  HeroNoImage,
  ImageContent,
  LinkContainer,
  OptionsRow,
  SellingAgentLogos,
  TextBox,
  TextBoxBottom,
  TextBoxTop,
  UnhideContainer,
  UnhidePropertyButton,
  ViewsPill,
  FooterThumbImages,
  CentralThumbImages,
  ThumbImage,
  ContentInner,
  TierBadge,
  SummaryRow,
  SummaryItem,
  Tagline,
  PinnedTagLine,
} from './PropertyBox.style';
import PropertySliderBox from './PropertyBoxSlider';
import PropertyDistance from './PropertyDistance';
import PropertyFavourite from './PropertyFavourite';
import PropertyHide from './PropertyHide';
import PropertySlideshowBar from './PropertySlideshowBar';
import ShowHomesBox from './ShowHomesBox';

export interface Props {
  property: Property;
  href?: string;
  onFavourite?: () => (favourited: boolean, propertyId: number) => Promise<boolean>;
  onHideToggle?: () => (hidden: boolean, propertyId: number) => Promise<boolean>;
  onUnhide?: (propertyId: number) => Promise<void>;
  onEmailClick?: (property: Property) => void;
  onCallClick?: (property: Property) => void;
  overlay?: React.ReactElement;
  locationDetail?: PropertySearchResult['locationDetail'];
  imageLocator?: string;
  showOpenViewingLabel?: boolean;
  useWideDisplay?: boolean;
  pinnedPropertyType?: boolean;
  useSlider?: boolean;
  loading?: 'lazy' | 'eager';
  disableWideMode?: boolean;
  onClick?: () => void;
  hideBriefText?: boolean;
  useEnhancedBranding?: boolean;
  hideBadge?: boolean;
  hideFooter?: boolean;
  getReplaceUrl?: (url: string) => string;
}

const PropertyBox: FunctionComponent<Props> = (props) => {
  const { property, overlay } = props;
  const useWideDisplay = props.useWideDisplay && !props.disableWideMode;
  const theme = useTheme();
  const [heroImage] = getMainImage(property, props.imageLocator);
  const showOpenViewingLabel = props.showOpenViewingLabel && property.openViewing;
  const showOpenViewingOverlay = !showOpenViewingLabel && property.openViewing && !property.openViewing.past;
  const canHide = props.onHideToggle && property.type === 'property';
  const canUnhide = props.onUnhide && property.type === 'property';
  const canFavourite = props.onFavourite && property.type === 'property';
  const sellingAgent = getSellingAgent(property);
  const showHighlightedDevelopmentCarousel = !!props.useWideDisplay && isHighlightedDevelopment(property);
  const isRentalDev = props.property.development && props.property.saleType.key === 'rent';
  const canEmail =
    props.onEmailClick &&
    (showHighlightedDevelopmentCarousel || props.property.enhancedBranding || isRentalDev) &&
    (sellingAgent?.emailEnabled || sellingAgent?.email);
  const canCall =
    props.onCallClick && (showHighlightedDevelopmentCarousel || props.property.enhancedBranding || isRentalDev);
  const isDevelopment = !!(property.development || property.type === 'development');
  const developer = property.account.developer && !property.account.privateDeveloper ? property.account : undefined;
  const agents = property.agents?.filter((a) => a.agent);
  const sellingAgentsWithLogos = agents?.filter((a) => !!a.logo?.standard);
  const accountLogos = getAgentLogos(property);
  const thumbImages = getThumbImages(property, 4, props.imageLocator);
  const isGold = !!(useWideDisplay && platform.type !== 'propertynews' && property.listingTier === 'GOLD');
  const isSilver = !!(useWideDisplay && platform.type !== 'propertynews' && property.listingTier === 'SILVER');
  const isBronze = !!(useWideDisplay && platform.type !== 'propertynews' && property.listingTier === 'BRONZE');
  const isPrime = !!(
    useWideDisplay &&
    platform.type !== 'propertynews' &&
    !!property.listingTier &&
    property.listingTier !== 'STANDARD'
  );
  const hasEnhancedBranding = !!(props.useEnhancedBranding && (property.enhancedBranding || isRentalDev));
  const { srpBrandingBgColour, srpBrandingTextColour, pdpBrandingLinkColour } =
    (props.useEnhancedBranding &&
      (props.property.enhancedBranding || isPrime || isRentalDev) &&
      props.property.enhancedBrandingColours) ||
    {};
  const maxMobileThumbImages = props.pinnedPropertyType ? 4 : property.listingTier === 'GOLD' ? 3 : 2;

  const handleEmailClick: MouseEventHandler = (e) => {
    if (props.onEmailClick) {
      e.preventDefault();
      props.onEmailClick(property);
    }
  };

  const handleCallClick: MouseEventHandler = (e) => {
    if (props.onCallClick) {
      e.preventDefault();
      props.onCallClick(property);
    }
  };

  return (
    <Container
      $useWideDisplay={useWideDisplay}
      data-testid={`property-box${props.pinnedPropertyType && props.getReplaceUrl ? '-pinned' : ''}`}
    >
      {showOpenViewingLabel && property.openViewing?.text && <StartEndTime text={property.openViewing.text} />}

      {!!props.property.distance && (
        <PropertyDistance distance={props.property.distance} locationDetail={props.locationDetail} />
      )}

      {overlay}

      <LinkContainer
        href={props.href || property.path}
        prefetch={false}
        onClick={props.onClick}
        $border={!!srpBrandingBgColour || showHighlightedDevelopmentCarousel}
        $useWideDisplay={useWideDisplay}
        $isGold={isGold}
        style={{ borderColor: srpBrandingBgColour || GOLD }}
        $disableHover={showHighlightedDevelopmentCarousel}
      >
        {isGold && !props.pinnedPropertyType && (
          <AgentHeader style={{ backgroundColor: srpBrandingBgColour }}>
            {!!sellingAgent && (
              <Text fontSize={12} color={srpBrandingTextColour} style={{ flex: 1 }}>
                Marketed by {sellingAgent.organisation}
              </Text>
            )}

            {accountLogos &&
              accountLogos.length > 0 &&
              accountLogos.map((logo) => (
                <img src={logo.url} key={logo.url} alt={`${sellingAgent?.organisation} Logo`} loading="lazy" />
              ))}
          </AgentHeader>
        )}

        <Content $useWideDisplay={useWideDisplay}>
          <ImageContent
            $useWideDisplay={useWideDisplay}
            $isPrime={isSilver || isGold || isBronze}
            $showPinnedDevelopmentPropertyCarousel={showHighlightedDevelopmentCarousel}
          >
            {isPrime && (
              <TierBadge style={{ backgroundColor: PRIME_BG_COLOR_MAP[property.listingTier] }}>
                PRIME | <span style={{ color: PRIME_COLOR_MAP[property.listingTier] }}>{property.listingTier}</span>
              </TierBadge>
            )}

            {showHighlightedDevelopmentCarousel && (
              <PinnedDevelopmentPropertyCarousel
                properties={property.properties!}
                imageLocator={props.imageLocator}
                loading={props.loading}
              />
            )}

            {!showHighlightedDevelopmentCarousel && !heroImage && (
              <HeroNoImage src={noPhoto.src} alt="No photo available" />
            )}

            {!showHighlightedDevelopmentCarousel && !!heroImage && (
              <PropertySliderBox
                property={property}
                imageLocator={props.imageLocator}
                useSlider={props.useSlider}
                loading={props.loading}
              />
            )}

            <PropertyBanner property={property} highlighted={showHighlightedDevelopmentCarousel} />

            {!showOpenViewingOverlay && !!property.viewsInLast7Days && (
              <ViewsPill>
                <b>{formatNumber(property.viewsInLast7Days)}</b> views this week
              </ViewsPill>
            )}

            {showOpenViewingOverlay && property.openViewing && (
              <OpenViewingOverlay
                openViewing={property.openViewing}
                themeName="dark"
                containerStyle={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                }}
              />
            )}
          </ImageContent>

          <ContentInner>
            {props.pinnedPropertyType && props.property.tagLine && (
              <PinnedTagLine>
                <Tagline className="pp-background-color" fontSize={14} data-testid="property-box-tagline">
                  {props.property.tagLine}
                </Tagline>
              </PinnedTagLine>
            )}
            <TextBox>
              <TextBoxTop
                $useWideDisplay={useWideDisplay}
                $extraPadding={!isGold && property.type !== 'development'}
                $pinnedProperty={!!props.pinnedPropertyType}
              >
                <PropertyBadge
                  property={property}
                  useWideDisplay={props.useWideDisplay}
                  hideBadge={props.hideBadge}
                  containerStyle={{ marginBottom: 10, marginRight: canFavourite ? 70 : undefined }}
                />

                <PropertyPrice
                  containerStyle={{ marginBottom: 12, fontWeight: 600 }}
                  price={property.price}
                  alternativeCurrencyPrice={property.alternativeCurrencyPrice}
                  fontSize={34}
                  status={property.status}
                  comingSoonText={property.comingSoonText}
                  primaryColor={isGold ? pdpBrandingLinkColour : theme.textDark}
                  prefixColor={theme.textDark}
                  prefixBreak
                />

                <SubHeading
                  color={theme.textMid}
                  fontSize={16}
                  mb={12}
                  fontWeight="600"
                  data-testid="property-box-address"
                >
                  {property.displayAddress.replace(/\s+/g, ' ')}
                </SubHeading>

                {!!props.property.briefText && !props.hideBriefText && !props.pinnedPropertyType && (
                  <Text fontSize={14} color={theme.textLight} mt={10} mb={10}>
                    {props.property.briefText}
                  </Text>
                )}

                <AttributePills property={property} />

                {(isSilver || isGold || props.pinnedPropertyType) && (
                  <SummaryRow>
                    {!!property.numBedrooms && (
                      <SummaryItem
                        className={props.pinnedPropertyType ? 'pp-background-color' : undefined}
                        $padding={props.pinnedPropertyType}
                      >
                        <FontAwesomeIcon
                          icon={faBedAlt}
                          color={theme.textDark}
                          size={props.pinnedPropertyType ? 18 : 22}
                        />
                        <Text
                          color={props.pinnedPropertyType ? theme.textMid : theme.textLight}
                          fontSize={props.pinnedPropertyType ? 14 : undefined}
                        >
                          {property.numBedrooms}
                        </Text>
                      </SummaryItem>
                    )}

                    {!!property.numBathrooms && (
                      <SummaryItem
                        className={props.pinnedPropertyType ? 'pp-background-color' : undefined}
                        $padding={props.pinnedPropertyType}
                      >
                        <FontAwesomeIcon
                          icon={faBath}
                          color={theme.textDark}
                          size={props.pinnedPropertyType ? 18 : 22}
                        />
                        <Text
                          color={props.pinnedPropertyType ? theme.textMid : theme.textLight}
                          fontSize={props.pinnedPropertyType ? 14 : undefined}
                        >
                          {property.numBathrooms}
                        </Text>
                      </SummaryItem>
                    )}

                    {!!property.numReceptionRooms && (
                      <SummaryItem
                        className={props.pinnedPropertyType ? 'pp-background-color' : undefined}
                        $padding={props.pinnedPropertyType}
                      >
                        <FontAwesomeIcon
                          icon={faCouch}
                          color={theme.textDark}
                          size={props.pinnedPropertyType ? 18 : 22}
                        />
                        <Text
                          color={props.pinnedPropertyType ? theme.textMid : theme.textLight}
                          fontSize={props.pinnedPropertyType ? 14 : undefined}
                        >
                          {property.numReceptionRooms}
                        </Text>
                      </SummaryItem>
                    )}
                  </SummaryRow>
                )}

                {property.ber && <EnergyRatingIcon property={property} hideEpcText />}

                <ShowHomesBox property={property} fontSize={14} />

                {props.pinnedPropertyType && props.getReplaceUrl && (
                  <PropertySlideshowBar property={property} getReplaceUrl={props.getReplaceUrl} />
                )}
              </TextBoxTop>

              {(canCall || canEmail) && (
                <TextBoxBottom>
                  <ContactText
                    style={{
                      backgroundColor: isGold ? srpBrandingBgColour : undefined,
                      color: isGold ? srpBrandingTextColour : undefined,
                    }}
                  >
                    Contact {sellingAgent?.organisation}
                  </ContactText>

                  <BottomRow>
                    {canCall && (
                      <button type="button" onClick={handleCallClick} data-testid="propertyBoxCall">
                        <FontAwesomeIcon icon={faPhone} size={22} />
                        Call
                      </button>
                    )}

                    {canEmail && (
                      <button type="button" onClick={handleEmailClick} data-testid="propertyBoxEmail">
                        <FontAwesomeIcon icon={faEnvelope} size={22} style={{ marginTop: 1 }} />
                        Email
                      </button>
                    )}
                  </BottomRow>
                </TextBoxBottom>
              )}

              {!canCall && !canEmail && isDevelopment && !props.hideFooter && (
                <DevelopmentBoxFiller $useWideDisplay={useWideDisplay} />
              )}

              <OptionsRow $useWideDisplay={useWideDisplay && !props.pinnedPropertyType}>
                {canHide && (
                  <PropertyHide
                    hidden={property.hidden}
                    onHideToggle={(hidden) => props.onHideToggle!()(hidden, property.id)}
                    // unique key needed in infinite grid, once item above is removed from list
                    // it would inherit its hidden state value in PropertyHide
                    key={`hide-${property.id}`}
                    showLabel
                  />
                )}
                {canFavourite && (
                  <PropertyFavourite
                    favourited={property.shortlisted}
                    // @ts-ignore
                    onFavourite={(favourited) => props.onFavourite()(favourited, property.id)}
                    // unique key needed in infinite grid, once item above is removed from list
                    // it would inherit its favourited state value in PropertyFavourite
                    key={property.id}
                    showLabel
                    containerStyle={{ marginLeft: 10 }}
                  />
                )}
              </OptionsRow>

              {!props.hideFooter && !isGold && (
                <AccountLogos $useWideDisplay={useWideDisplay} $enhancedBranding={hasEnhancedBranding}>
                  {hasEnhancedBranding && agents && agents.length === 1 && (
                    <AccountLogo
                      $useWideDisplay={useWideDisplay}
                      style={{
                        backgroundColor: agents[0].logo?.standardBackgroundColour,
                        backgroundImage: `url(${agents[0].logo?.standard})`,
                      }}
                    />
                  )}

                  {!hasEnhancedBranding && isDevelopment && (
                    <DeveloperLogos $useWideDisplay={useWideDisplay}>
                      {!!developer && <img src={developer.logo?.standard} alt="" />}

                      {sellingAgentsWithLogos && sellingAgentsWithLogos.length > 0 && (
                        <div>
                          <p>Selling Agents</p>
                          <SellingAgentLogos $useWideDisplay={useWideDisplay}>
                            {sellingAgentsWithLogos.map((agent) => {
                              return <img key={agent.logo?.standard} src={agent.logo?.standard} alt="" />;
                            })}
                          </SellingAgentLogos>
                        </div>
                      )}
                    </DeveloperLogos>
                  )}

                  {!hasEnhancedBranding &&
                    !isDevelopment &&
                    agents &&
                    agents.map((agent) => <img key={agent.logo?.standard} src={agent.logo?.standard} alt="" />)}
                </AccountLogos>
              )}

              {canUnhide && (
                <UnhideContainer>
                  <UnhidePropertyButton
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      props.onUnhide!(property.id);
                    }}
                    key={`unhide-${property.id}`}
                    testID="unhide-property"
                    icon={faEyeSlash}
                  >
                    unhide
                  </UnhidePropertyButton>
                </UnhideContainer>
              )}
            </TextBox>

            {(isSilver || isGold || props.pinnedPropertyType) && (
              <CentralThumbImages
                tier={property.listingTier}
                $useWideDisplay={useWideDisplay}
                pinnedProperty={!!props.pinnedPropertyType}
              >
                {thumbImages &&
                  thumbImages.slice(0, maxMobileThumbImages).map((thumbImage, index) => (
                    <ThumbImage key={thumbImage.url}>
                      <LazyImage
                        src={thumbImage.url}
                        blurHash={thumbImage.blurHash}
                        alt={`${property.displayAddress} photo ${index + 2}`}
                        aria-hidden="true"
                        width={340}
                        height={255}
                        loading={props.loading}
                      />
                    </ThumbImage>
                  ))}
              </CentralThumbImages>
            )}
          </ContentInner>
        </Content>

        {isGold && (
          <FooterThumbImages>
            {thumbImages &&
              thumbImages.map((thumbImage, index) => (
                <ThumbImage key={thumbImage.url} data-testid="property-thumb-image">
                  <LazyImage
                    src={thumbImage.url}
                    blurHash={thumbImage.blurHash}
                    alt={`${property.displayAddress} photo ${index + 2}`}
                    aria-hidden="true"
                    width={340}
                    height={255}
                    loading={props.loading}
                  />
                </ThumbImage>
              ))}
          </FooterThumbImages>
        )}
      </LinkContainer>
    </Container>
  );
};

export default React.memo(PropertyBox);
